import { enforceRateLimit } from "./rate-limit.server";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { limitsFor, PLANS } from "./plans";
import { clampPromotionDays, promotionCap } from "./promotion-days";

const startOfMonth = () => {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString();
};

async function readPromotionStatus(
  context: { supabase: any; userId: string },
  asPlan?: string | null,
) {
  const { readPlanAndUsageAs } = await import("./plans.server");
  const { plan, unlimited } = await readPlanAndUsageAs(
    context.supabase as never,
    context.userId,
    (asPlan ?? null) as any,
  );
  const { data: rows } = await context.supabase
    .from("ad_promotions")
    .select("days")
    .eq("user_id", context.userId)
    .is("cancelled_at", null)
    .gte("created_at", startOfMonth());

  // Kvoten räknas i dygn: antal lyft per månad gånger dygn per lyft.
  const used = ((rows ?? []) as Array<{ days: number }>).reduce((sum, r) => sum + (r.days ?? 1), 0);
  const step = limitsFor(plan, unlimited).promotionDays;
  // Obegränsade konton får en stor men läsbar kvot i stället för MAX_SAFE_INTEGER.
  const limit = unlimited ? 9999 : limitsFor(plan, unlimited).promotionsPerMonth * step;
  return {
    plan,
    unlimited,
    used,
    limit,
    /** Minsta längd på ett lyft, och det steg längden väljs i. */
    step,
    days: step,
    remaining: Math.max(0, limit - used),
  };
}

/** Hur många annonslyft användaren har kvar den här månaden. */
export const getPromotionStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({ asPlan: z.enum(["free", "plus", "pro", "expert"]).nullish() })
      .partial()
      .parse(input ?? {}),
  )
  .handler(async ({ data, context }) => readPromotionStatus(context as any, data?.asPlan ?? null));

/** Lyfter fram en egen annons högst upp i listan under planens antal dygn. */
export const promoteAd = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        slug: z.string().min(1),
        days: z.number().int().min(1).max(365).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await enforceRateLimit("promotion_create", context.userId);
    const ctx = context as any;
    const [status, adRes] = await Promise.all([
      readPromotionStatus(ctx),
      ctx.supabase
        .from("ads")
        .select("id, promoted_until, expires_at")
        .eq("slug", data.slug)
        .eq("user_id", ctx.userId)
        .maybeSingle(),
    ]);

    if (status.limit <= 0) {
      return {
        ok: false as const,
        message: "Uppgradera till Plus eller Pro för att kunna lyfta fram dina annonser.",
        status,
      };
    }
    if (status.remaining <= 0) {
      return {
        ok: false as const,
        message: `Du har använt dina ${status.limit} lyftdagar den här månaden.`,
        status,
      };
    }

    const ad = adRes?.data;
    if (!ad) throw new Error("Annonsen hittades inte eller tillhör inte dig.");

    const now = Date.now();
    const current = ad.promoted_until ? new Date(ad.promoted_until).getTime() : 0;
    if (current > now) {
      return { ok: false as const, message: "Annonsen är redan framlyft just nu.", status };
    }

    const step = status.step || 1;
    // Ett lyft kan aldrig vara längre än annonsen faktiskt visas.
    const adDaysLeft = ad.expires_at
      ? Math.floor((new Date(ad.expires_at).getTime() - now) / 86400000)
      : null;
    const cap = promotionCap(status.remaining, adDaysLeft);
    // Lyft bokas alltid i hela steg, inom månadskvoten och det gemensamma taket.
    const days = clampPromotionDays(step, cap, data.days ?? step);
    if (days < 1) {
      return {
        ok: false as const,
        message:
          adDaysLeft != null && adDaysLeft < step
            ? `Annonsen visas bara ${Math.max(0, adDaysLeft)} dygn till – förnya den först, ett lyft bokas i steg om ${step} dygn.`
            : `Du har bara ${status.remaining} lyftdagar kvar den här månaden och ett lyft bokas i steg om ${step} dygn.`,
        status,
      };
    }
    const until = new Date(now + days * 24 * 60 * 60 * 1000).toISOString();
    const [updateRes] = await Promise.all([
      ctx.supabase
        .from("ads")
        .update({ promoted_until: until })
        .eq("id", ad.id)
        .eq("user_id", ctx.userId),
      ctx.supabase.from("ad_promotions").insert({ ad_id: ad.id, user_id: ctx.userId, days }),
    ]);
    if (updateRes?.error) throw new Error(`Kunde inte lyfta annonsen: ${updateRes.error.message}`);

    return {
      ok: true as const,
      days,
      promotedUntil: until,
      status: { ...status, used: status.used + days, remaining: status.remaining - days },
    };
  });

/** Alla annonslyft användaren har startat, med status aktiv, avbruten eller avslutad. */
export const listMyPromotions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as any;
    const { data, error } = await ctx.supabase
      .from("ad_promotions")
      .select("id, days, created_at, cancelled_at, ad_id, ads(slug, title, promoted_until, image_urls)")
      .eq("user_id", ctx.userId)
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);

    const now = Date.now();
    return (data ?? []).map((row: any) => {
      const started = new Date(row.created_at).getTime();
      const endsAt = new Date(started + row.days * 24 * 60 * 60 * 1000).toISOString();
      const cancelled = Boolean(row.cancelled_at);
      return {
        id: row.id as string,
        days: row.days as number,
        startedAt: row.created_at as string,
        endsAt,
        cancelledAt: (row.cancelled_at ?? null) as string | null,
        slug: (row.ads?.slug ?? null) as string | null,
        title: (row.ads?.title ?? "Borttagen annons") as string,
        image: ((row.ads?.image_urls ?? [])[0] ?? null) as string | null,
        active: !cancelled && new Date(endsAt).getTime() > now,
        cancelled,
      };
    });
  });

/** Avbryter ett pågående lyft och lämnar tillbaka lyftpoängen. */
export const cancelPromotion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().min(1) }).parse(input))
  .handler(async ({ data, context }) => {
    const ctx = context as any;
    const { data: row } = await ctx.supabase
      .from("ad_promotions")
      .select("id, ad_id, days, created_at, cancelled_at")
      .eq("id", data.id)
      .eq("user_id", ctx.userId)
      .maybeSingle();
    if (!row) throw new Error("Kampanjen hittades inte.");
    if (row.cancelled_at) return { ok: false as const, message: "Kampanjen är redan avbruten." };

    const endsAt = new Date(row.created_at).getTime() + row.days * 24 * 60 * 60 * 1000;
    if (endsAt <= Date.now()) {
      return {
        ok: false as const,
        message: "Kampanjen är redan avslutad, inget lyft att få tillbaka.",
      };
    }

    const { error } = await ctx.supabase
      .from("ad_promotions")
      .update({ cancelled_at: new Date().toISOString() })
      .eq("id", row.id)
      .eq("user_id", ctx.userId);
    if (error) throw new Error(`Kunde inte avbryta kampanjen: ${error.message}`);

    await ctx.supabase
      .from("ads")
      .update({ promoted_until: null })
      .eq("id", row.ad_id)
      .eq("user_id", ctx.userId);

    return {
      ok: true as const,
      message: "Kampanjen är avbruten och lyftet är tillbaka i din kvot.",
    };
  });

/** Tar bort ett pågående framlyft för en av mina annonser och lämnar tillbaka lyftdagarna. */
export const cancelPromotionForAd = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data, context }) => {
    const ctx = context as any;
    const { data: ad } = await ctx.supabase
      .from("ads")
      .select("id")
      .eq("slug", data.slug)
      .eq("user_id", ctx.userId)
      .maybeSingle();
    if (!ad) throw new Error("Annonsen hittades inte eller tillhör inte dig.");

    const { refundActivePromotionsForAd } = await import("./promotions.server");
    const refunded = await refundActivePromotionsForAd(ctx.supabase, ctx.userId, ad.id);
    await ctx.supabase
      .from("ads")
      .update({ promoted_until: null })
      .eq("id", ad.id)
      .eq("user_id", ctx.userId);

    return {
      ok: true as const,
      refunded,
      message: refunded
        ? "Framlyftet är borttaget och lyftdagarna är tillbaka i din kvot."
        : "Annonsen är inte framlyft längre.",
    };
  });
